import React from 'react';
import {
    Caption,
    pxToRem,
    TextSelectorInput,
    useDeviceTypeContext,
    useStyles,
} from '@archipro-design/aria';
import * as S from './SuburbSearchInput.style';
import { CloseLine, SearchLine } from '@archipro-design/icons';
import { useDebounceEffect } from 'ahooks';
import useLocalities from '../../hook/use-localities';
import formatLocality from '../../util/format-locality';
import type { Locality } from '../../type/locality';
import CurrentLocation from './CurrentLocation';

interface DropdownItemType {
    key: number;
    header: string;
    value: number;
}

interface Props {
    locality?: Locality | null;
    onChange?: (locality: Locality | null) => void;
}

const mapDropdownOption = (
    locality?: Locality | null
): DropdownItemType | null =>
    locality
        ? {
              key: locality.ID,
              header: formatLocality(locality),
              value: locality.ID,
          }
        : null;

const SuburbSearchInput = ({ locality, onChange }: Props) => {
    const { css } = useStyles();
    const { mobile } = useDeviceTypeContext();
    const [text, setText] = React.useState(
        locality ? formatLocality(locality) : ''
    );
    const [value, setValue] = React.useState<DropdownItemType | null>(
        mapDropdownOption(locality)
    );

    const { results, search, loading } = useLocalities();

    useDebounceEffect(
        () => {
            text && !text.includes(',') && search(text);
        },
        [text],
        {
            wait: 200,
        }
    );

    const dropdownItems = (
        locality && !results.length ? [locality] : results
    ).map(mapDropdownOption);

    React.useEffect(() => {
        const option = locality
            ? {
                  key: locality.ID,
                  header: formatLocality(locality),
                  value: locality.ID,
              }
            : null;
        setValue(option);
        setText(option?.header ?? '');
    }, [locality]);

    const onDropdownChange = (selected: DropdownItemType | null) => {
        if (!selected) {
            setText('');
            setValue(null);
            return;
        }

        onChange?.(results.find((item) => item.ID === selected.value) || null);
        setValue(value);
    };

    const onLocate = (loc: Locality | null) => {
        if (loc) {
            setValue(mapDropdownOption(loc));
            onChange?.(loc);
        }
    };

    return (
        <div className={css(S.SuburbSearchInput)}>
            <TextSelectorInput
                input={{
                    label: {
                        children: (
                            <Caption variant={mobile ? '03s' : '02'}>
                                Deliver to
                            </Caption>
                        ),
                    },
                }}
                dropdown={{
                    items: dropdownItems,
                    search: true,
                    placeholder: 'Enter suburb or postcode',
                    searchQuery: text,
                    value,
                    clearable: false,
                    loading,
                    offset: mobile ? undefined : [0, -3],
                    onChange: (_, props) => {
                        props &&
                            onDropdownChange(props.value as DropdownItemType);
                    },
                    onSearchQueryChange: (_, props) => {
                        props.searchQuery
                            ? setText(props.searchQuery)
                            : setValue(null);
                    },
                    positionFixed: true,
                    noResultsMessage: 'No results found.',
                    clearIndicator: value ? (
                        <CloseLine
                            variables={{
                                '1xSize': mobile ? pxToRem(18) : pxToRem(24),
                            }}
                        />
                    ) : (
                        <span></span>
                    ),
                    loadingMessage: 'Loading...',
                }}
                suffixIcons={[
                    <SearchLine
                        key="icon-search"
                        variables={{
                            '1xSize': mobile ? pxToRem(18) : pxToRem(24),
                        }}
                    />,
                ]}
                variables={{
                    maxDropdownHeight: pxToRem(240),
                }}
            />
            <CurrentLocation onLocate={onLocate} />
        </div>
    );
};

export default SuburbSearchInput;
